import React, { useEffect, useMemo, useState } from "react";
import { Funnel, Search, Plus, Pencil, Trash2, X } from "lucide-react";
import Layout from "../../components/Layout";
import {
  BotModeratorService,
  SensitiveTermItem,
} from "../../services/botModeratorService";
import "./../../styles/member/botmoderator.css";
import ConfirmDelete from "../../components/ConfirmDelete";

const BotModerator: React.FC = () => {
  const [items, setItems] = useState<SensitiveTermItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{
    term: string;
    isRegex: boolean;
    severity: "warn" | "block";
    appliesTo: "text" | "image" | "both";
  }>({ term: "", isRegex: false, severity: "warn", appliesTo: "text" });
  const [editing, setEditing] = useState<SensitiveTermItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [query, setQuery] = useState("");
  const [filterSeverity, setFilterSeverity] = useState<
    "all" | "warn" | "block"
  >("all");
  const [filterRegex, setFilterRegex] = useState<"all" | "regex" | "plain">(
    "all"
  );
  const [openSeverity, setOpenSeverity] = useState(false);
  const [openRegex, setOpenRegex] = useState(false);
  const severityRef = React.useRef<HTMLDivElement | null>(null);
  const regexRef = React.useRef<HTMLDivElement | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const getSeverityLabel = (s: "all" | "warn" | "block") => {
    if (s === "all") return "Tất cả";
    if (s === "warn") return "Cảnh báo";
    return "Chặn";
  };
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((it) => {
        if (filterSeverity !== "all" && it.severity !== filterSeverity)
          return false;
        if (filterRegex === "regex" && !it.isRegex) return false;
        if (filterRegex === "plain" && it.isRegex) return false;
        if (q && !it.term.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => {
        // Sắp xếp theo thứ tự ưu tiên: warn trước, block sau
        if (a.severity === "warn" && b.severity === "block") return -1;
        if (a.severity === "block" && b.severity === "warn") return 1;
        // Nếu cùng mức độ, sắp xếp theo thời gian tạo
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
  }, [items, query, filterSeverity, filterRegex]);

  const allSelected = useMemo(
    () =>
      filteredItems.length > 0 && selectedIds.length === filteredItems.length,
    [filteredItems.length, selectedIds]
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await BotModeratorService.listSensitiveTerms();
      setItems(data);
    } catch (e: any) {
      setError(e?.message || "Không thể tải danh sách");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        openSeverity &&
        severityRef.current &&
        !severityRef.current.contains(target)
      ) {
        setOpenSeverity(false);
      }
      if (openRegex && regexRef.current && !regexRef.current.contains(target)) {
        setOpenRegex(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [openSeverity, openRegex]);

  const resetForm = () =>
    setForm({ term: "", isRegex: false, severity: "warn", appliesTo: "text" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      if (editing) {
        await BotModeratorService.updateSensitiveTerm(editing.id, form);
        setEditing(null);
      } else {
        await BotModeratorService.addSensitiveTerm(form);
      }
      resetForm();
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.message || "Lưu thất bại");
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (item: SensitiveTermItem) => {
    setEditing(item);
    setForm({
      term: item.term,
      isRegex: item.isRegex,
      severity: item.severity,
      appliesTo: (item as any).appliesTo || "text",
    });
    setShowModal(true);
  };

  const onDelete = (id: number) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const onBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setDeleteId(-1);
    setShowDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId == null) return;
    try {
      setLoading(true);
      if (deleteId === -1) {
        await Promise.all(
          selectedIds.map((id) => BotModeratorService.deleteSensitiveTerm(id))
        );
        setSelectedIds([]);
      } else {
        await BotModeratorService.deleteSensitiveTerm(deleteId);
        setSelectedIds((prev) => prev.filter((x) => x !== deleteId));
      }
      await load();
      setShowDelete(false);
      setDeleteId(null);
    } catch (e: any) {
      setError(e?.message || "Xóa thất bại");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="article-page">
        {/* Header giống Article */}
        <div className="article-header">
          <div>
            <h2>Bot Kiểm duyệt</h2>
            <p>Quản trị danh sách phục vụ kiểm duyệt</p>
          </div>
          <div>
            <button
              className="btn-primary btn-add-custom"
              onClick={() => {
                setEditing(null);
                resetForm();
                setShowModal(true);
              }}
            >
              <Plus size={16} /> &nbsp;Thêm mới
            </button>
          </div>
        </div>

        {error && (
          <div className="botmod__alert botmod__alert--error">{error}</div>
        )}

        {/* Bộ lọc giống Article */}
        <div className="article-filter ">
          <div className="article-filter-title">
            <Funnel size={20} /> Bộ Lọc
          </div>
          <div className="article-filter-bar">
            <div className="filter-dropdown" ref={severityRef}>
              <button
                className="dropdown-trigger"
                type="button"
                onClick={() => setOpenSeverity((v) => !v)}
              >
                Mức ({getSeverityLabel(filterSeverity)})
                <span className="dropdown-arrow">▾</span>
              </button>
              {openSeverity && (
                <div className="dropdown-menu">
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setFilterSeverity("all");
                      setOpenSeverity(false);
                    }}
                  >
                    Tất cả
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setFilterSeverity("warn");
                      setOpenSeverity(false);
                    }}
                  >
                    Cảnh báo
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setFilterSeverity("block");
                      setOpenSeverity(false);
                    }}
                  >
                    Chặn
                  </div>
                </div>
              )}
            </div>
            <div className="filter-dropdown" ref={regexRef}>
              <button
                className="dropdown-trigger"
                type="button"
                onClick={() => setOpenRegex((v) => !v)}
              >
                Kiểu (
                {filterRegex === "all"
                  ? "Tất cả"
                  : filterRegex === "regex"
                  ? "Regex"
                  : "Chuỗi thường"}
                )<span className="dropdown-arrow">▾</span>
              </button>
              {openRegex && (
                <div className="dropdown-menu">
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setFilterRegex("all");
                      setOpenRegex(false);
                    }}
                  >
                    Tất cả
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setFilterRegex("plain");
                      setOpenRegex(false);
                    }}
                  >
                    Chuỗi thường
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setFilterRegex("regex");
                      setOpenRegex(false);
                    }}
                  >
                    Regex
                  </div>
                </div>
              )}
            </div>
            <div className="search-box">
              <Search className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Tìm kiếm từ..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setQuery((p) => p)}
              />
            </div>
          </div>
        </div>

        {/* Bulk actions khi có chọn nhiều */}
        {selectedIds.length > 0 && (
          <div className="cat-bulk-bar">
            <span>Đã chọn {selectedIds.length}</span>
            <div className="card-actions">
              <button
                type="button"
                className="status-reject"
                onClick={onBulkDelete}
                disabled={loading}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}

        <div className="categories-table-card">
          <div className="categories-table-scroll">
            <table className="categories-table botmod__table">
              <thead>
                <tr>
                  <th className="botmod__col botmod__col--center botmod__col--checkbox">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => {
                        if (allSelected) setSelectedIds([]);
                        else setSelectedIds(filteredItems.map((i) => i.id));
                      }}
                      aria-label="Chọn tất cả"
                    />
                  </th>
                  <th className="botmod__col botmod__col--center">ID</th>
                  <th className="botmod__col botmod__col--term">Từ/Regex</th>
                  <th className="botmod__col botmod__col--center botmod__col--regex">
                    Regex?
                  </th>
                  <th className="botmod__col botmod__col--center botmod__col--level">
                    Mức
                  </th>
                  <th className="botmod__col botmod__col--center botmod__col--actions">
                    Hành động
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it, index) => (
                  <tr key={it.id}>
                    <td className="botmod__col--center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(it.id)}
                        onChange={() =>
                          setSelectedIds((prev) =>
                            prev.includes(it.id)
                              ? prev.filter((x) => x !== it.id)
                              : [...prev, it.id]
                          )
                        }
                        aria-label={`Chọn ${it.term}`}
                      />
                    </td>
                    <td className="botmod__col--center">
                      MBM{it.id.toString().padStart(3, "0")}
                    </td>
                    <td>{it.term}</td>
                    <td className="botmod__col--center">
                      {it.isRegex ? "✔" : ""}
                    </td>
                    <td className="botmod__col--center">
                      <span
                        className={
                          it.severity === "block"
                            ? "botmod__badge botmod__badge--block"
                            : "botmod__badge botmod__badge--warn"
                        }
                      >
                        {getSeverityLabel(it.severity as any)}
                      </span>
                    </td>
                    <td className="botmod__col--center">
                      <div className="card-actions botmod__row-actions">
                        <button
                          className="btn-edit"
                          title="Sửa"
                          onClick={() => onEdit(it)}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="btn-danger"
                          title="Xóa"
                          onClick={() => onDelete(it.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div
            className="botmod__modal-backdrop"
            onClick={() => !loading && setShowModal(false)}
          >
            <div className="botmod__modal" onClick={(e) => e.stopPropagation()}>
              <div className="botmod__modal-header">
                <h3>{editing ? "Cập nhật từ nhạy cảm" : "Thêm từ nhạy cảm"}</h3>
                <button
                  className="botmod__modal-close"
                  onClick={() => !loading && setShowModal(false)}
                  aria-label="Đóng"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={onSubmit} className="botmod__form">
                <div className="botmod__form-row">
                  <label className="botmod__label">Từ/Regex</label>
                  <input
                    type="text"
                    placeholder="Nhập từ cần kiểm duyệt hoặc mẫu regex"
                    value={form.term}
                    onChange={(e) => setForm({ ...form, term: e.target.value })}
                    required
                    className="botmod__input"
                    autoFocus
                  />
                </div>

                <div className="botmod__form-row">
                  <label className="botmod__label">Kiểu khớp</label>
                  <label className="botmod__checkbox">
                    <input
                      type="checkbox"
                      checked={form.isRegex}
                      onChange={(e) =>
                        setForm({ ...form, isRegex: e.target.checked })
                      }
                    />
                    Sử dụng Regex
                  </label>
                </div>

                <div className="botmod__form-row">
                  <label className="botmod__label">Mức xử lý</label>
                  <select
                    value={form.severity}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        severity: e.target.value as "warn" | "block",
                      })
                    }
                    className="botmod__select"
                  >
                    <option value="warn">Cảnh báo</option>
                    <option value="block">Chặn</option>
                  </select>
                </div>

                <div className="botmod__form-row">
                  <label className="botmod__label">Áp dụng cho</label>
                  <select
                    value={form.appliesTo}
                    onChange={(e) =>
                      setForm({ ...form, appliesTo: e.target.value as any })
                    }
                    className="botmod__select"
                  >
                    <option value="text">Nội dung văn bản</option>
                    <option value="image">Hình ảnh</option>
                    <option value="both">Cả hai</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn terms-action-btn"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {editing ? "Lưu thay đổi" : "Tạo mới"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmDelete
          isOpen={showDelete}
          title={deleteId === -1 ? "Xác nhận xóa nhiều từ" : "Xác nhận xóa từ"}
          message={(() => {
            if (deleteId === -1) {
              return (
                <span>
                  Bạn có chắc chắn muốn xóa <b>{selectedIds.length}</b> mục đã
                  chọn? Hành động này không thể hoàn tác.
                </span>
              );
            }
            const name = filteredItems.find((i) => i.id === deleteId)?.term;
            return name ? (
              <span>
                Bạn có chắc chắn muốn xóa từ <b>{name}</b>?
              </span>
            ) : (
              "Bạn có chắc chắn muốn xóa mục này?"
            );
          })()}
          confirmText="Xóa"
          cancelText="Hủy"
          isSubmitting={loading}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDelete(false);
            setDeleteId(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default BotModerator;
